<template>
  <v-card>
    <v-card-title class="title-card">
      <h4>
        <v-icon size="18" class="mx-2">fa-solid fa-users</v-icon>
        {{
          edit
            ? $t("components.views.settings.groups.edit_group")
            : $t("components.views.settings.groups.new_group")
        }}
      </h4>
      <v-spacer />
      <v-btn depressed text color="white" @click.stop="$emit('closeForm')">
        <v-icon small class="mr-1">fa-regular fa-rectangle-xmark</v-icon>
      </v-btn>
    </v-card-title>
    

    
    <v-card-text>
      <v-form v-model="valid" ref="form">
        <v-container fluid>
          <v-row>
            <v-col cols="12" class="d-flex align-items-center">
              <div class="icon-container">
                <v-icon color="#0b0045">fa-solid fa-users</v-icon>
              </div>
              <v-text-field v-model="group.name" label="Nombre del grupo" :rules="[required('Nombre del grupo')]"
                outlined dense required class="flex-grow-1" />
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-row>
            <v-col cols="12" class="d-flex justify-space-between align-center">
              <h5 class="section-title">
                {{ $t("components.views.settings.groups.add_members") }}
              </h5>
              <v-btn
                v-if="(edit && group.id) || (!edit && hasMembers)"
                color="#FF6B35"
                dark
                class="rounded-lg"
                small
                @click="openPrioritiesModal"
                :disabled="!canManagePriorities"
              >
                <v-icon small class="mr-1">fa-solid fa-star</v-icon>
                {{ $t("components.views.settings.groups.manage_priorities") }}
              </v-btn>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="d-flex align-items-center">
              <div class="icon-container">
                <v-icon color="#0b0045">fa-solid fa-building</v-icon>
              </div>
              <SelectAutocomplete
                :selected="selectedTransportCompanies"
                @input="selectedTransportCompanies = $event"
                :data="allTransportCompanies"
                :init="false"
                :loading="isLoadingData"
                item-text="businessName"
                item-value="businessName"
                return-object
                :label="$t('components.views.settings.groups.transport_company')"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="flex-grow-1"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="d-flex align-items-center">
              <div class="icon-container">
                <v-icon color="#0b0045">fa-solid fa-id-card</v-icon>
              </div>
              <SelectAutocomplete
                :selected="selectedPlates"
                @input="selectedPlates = $event"
                :data="allVehicles"
                :init="false"
                :loading="isLoadingData"
                item-text="licensePlate"
                item-value="licensePlate"
                return-object
                :label="$t('components.views.settings.groups.plate')"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="flex-grow-1"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="d-flex align-items-center">
              <div class="icon-container">
                <v-icon color="#0b0045">fa-solid fa-user</v-icon>
              </div>
              <SelectAutocomplete
                :selected="selectedDrivers"
                @input="selectedDrivers = $event"
                :data="allDrivers"
                :init="false"
                :loading="isLoadingData"
                item-text="fullName"
                item-value="idDriver"
                return-object
                :label="$t('components.views.settings.groups.driver')"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="flex-grow-1"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      

    </v-card-text>
    <v-divider />
    <v-card-actions class="d-flex justify-space-between">
      <v-btn v-if="edit" color="red darken-2" dark class="rounded-lg" @click.stop="showDeleteConfirmation = true">
        <v-icon small class="mr-1">fa-solid fa-trash</v-icon>
        {{ $t("components.views.settings.groups.delete_group") }}
      </v-btn>

      <v-spacer v-if="!edit"></v-spacer>

      <div class="d-flex">
        <v-btn color="grey" text class="rounded-lg mr-2" @click.stop="$emit('closeForm')">
          {{ $t("components.views.settings.groups.exit") }}
        </v-btn>

        <v-btn color="#FF6B35" dark class="rounded-lg" @click.stop="saveGroup">
          <v-icon small class="mr-1">{{ edit ? "fa-solid fa-edit" : "fa-solid fa-save" }}</v-icon>
          {{
            edit
              ? $t("components.views.settings.groups.edit_group_button")
              : $t("components.views.settings.groups.save_button")
          }}
        </v-btn>
      </div>
    </v-card-actions>

    <!-- Modal de confirmación para eliminar -->
    <v-dialog v-model="showDeleteConfirmation" max-width="400px" persistent>
      <v-card class="text-center pa-4">
        <v-card-text>
          <h3 class="mb-3">{{ $t("components.views.settings.groups.are_you_sure") }}</h3>
          <p class="mb-0">{{ $t("components.views.settings.groups.delete_warning") }}</p>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn color="grey" text class="rounded-lg mr-2" @click="showDeleteConfirmation = false">
            {{ $t("components.views.settings.groups.exit") }}
          </v-btn>
          <v-btn color="red darken-2" dark class="rounded-lg" @click="confirmDelete">
            {{ $t("components.views.settings.groups.yes_delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de prioridades -->
    <v-dialog v-model="showPrioritiesModal" max-width="900px" persistent>
      <ModalPriorities
        :key="prioritiesModalKey"
        :groupData="currentGroupData"
        :isEditMode="edit"
        @closeModal="closePrioritiesModal"
        @prioritiesUpdated="onPrioritiesUpdated"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import validaciones from "@/plugins/validate";

export default {
  components: {
    SelectAutocomplete: () => import("@/components/app/SelectAutocomplete"),
    ModalPriorities: () => import("@/components/views/Settings/Groups/Modals/ModalPriorities/ModalPriorities.vue"),
  },
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      ...validaciones,
      valid: false,
      showDeleteConfirmation: false,
      showPrioritiesModal: false,
      prioritiesModalKey: 0,
      group: {
        id: 0,
        name: "",
      },
      selectedTransportCompanies: [],
      selectedPlates: [],
      selectedDrivers: [],
      // Agregar las listas completas
      allTransportCompanies: [],
      allDrivers: [],
      allVehicles: [],
      isLoadingData: false,
      // Variable local para almacenar las prioridades configuradas
      configuredPriorities: {
        transportCompanies: [],
        drivers: [],
        vehicles: []
      }
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    idCompany() {
      let userMap = JSON.parse(this.user);
      return userMap.user.idCompany;
    },
    currentGroupData() {
      let members;
      
      // Si estamos editando, usar los datos del servidor
      if (this.edit && this.data?.members) {
        members = this.data.members;
      } 
      // Si estamos creando pero ya tenemos prioridades configuradas, usarlas
      else if (!this.edit && this.configuredPriorities) {
        const hasConfiguredPriorities = 
          (this.configuredPriorities.transportCompanies && this.configuredPriorities.transportCompanies.length > 0) ||
          (this.configuredPriorities.drivers && this.configuredPriorities.drivers.length > 0) ||
          (this.configuredPriorities.vehicles && this.configuredPriorities.vehicles.length > 0);
        
        if (hasConfiguredPriorities) {
          members = this.buildCurrentMembersWithPriorities();
        } else {
          members = this.buildCurrentMembers();
        }
      }
      // Si no hay nada configurado, construir desde cero
      else {
        members = this.buildCurrentMembers();
      }
      
      return {
        id: this.group.id,
        name: this.group.name,
        idCompany: this.group.idCompany || this.idCompany,
        members: members
      };
    },
    hasMembers() {
      return (this.selectedTransportCompanies && this.selectedTransportCompanies.length > 0) ||
             (this.selectedDrivers && this.selectedDrivers.length > 0) ||
             (this.selectedPlates && this.selectedPlates.length > 0);
    },
    canManagePriorities() {
      if (this.edit && this.group.id) {
        return true;
      }
      return this.hasMembers;
    }
  },
  methods: {
    ...mapActions("snackbar", ["showSnack"]),
    ...mapActions("loader", ["showLoader"]),

    async loadAllData() {
      this.isLoadingData = true;
      try {
        // Una sola petición a cada endpoint
        const [transportCompaniesResponse, driversResponse, vehiclesResponse] = await Promise.all([
          this.axios.get("/TransportCompany"),
          this.axios.get("/Drivers"),
          this.axios.get("/Vehicles")
        ]);

        this.allTransportCompanies = transportCompaniesResponse.data || [];
        this.allDrivers = (driversResponse.data || []).map(driver => ({
          ...driver,
          fullName: `${driver.name || ''} ${driver.lastName || ''}`.trim(),
          id: driver.idDriver
        }));
        this.allVehicles = vehiclesResponse.data || [];

        // Si estamos editando, preseleccionar los elementos
        if (this.edit && this.data.members) {
          this.loadSelectedItems();
        }
      } catch (error) {
        console.error("Error loading data:", error);
        this.showSnack({
          text: 'Error al cargar los datos',
          title: 'Error',
          name: 'error',
        });
      } finally {
        this.isLoadingData = false;
      }
    },

    loadSelectedItems() {
      // Preseleccionar empresas de transporte
      if (this.data.members.transportCompanies) {
        this.selectedTransportCompanies = this.data.members.transportCompanies.map(member => {
          return this.allTransportCompanies.find(company =>
            company.identification === member.identification ||
            company.idCompanyParent === member.id ||
            company.idCompany === member.id
          );
        }).filter(Boolean);
      }

      // Preseleccionar conductores
      if (this.data.members.drivers) {
        this.selectedDrivers = this.data.members.drivers.map(member => {
          return this.allDrivers.find(driver =>
            driver.idDriver === member.id ||
            driver.identification === member.identification
          );
        }).filter(Boolean);
      }

      // Preseleccionar vehículos
      if (this.data.members.vehicles) {
        this.selectedPlates = this.data.members.vehicles.map(member => {
          return this.allVehicles.find(vehicle =>
            vehicle.idVehicle === member.id ||
            vehicle.licensePlate === member.licensePlate
          );
        }).filter(Boolean);
      }
    },

    async saveGroup() {
      if (this.$refs.form.validate()) {
        this.showLoader(true);
        try {
          if (this.edit) {
            await this.updateGroup();
          } else {
            await this.createGroup();
          }
          this.$emit("reloadTable");
          this.$emit("closeForm");
        } catch (error) {
          this.showSnack({
            text:
              error.response?.data ||
              this.$t("components.views.settings.groups.error_saving_group"),
            title: this.$t("components.views.settings.groups.error"),
            name: "error",
          });
        } finally {
          this.showLoader(false);
        }
      }
    },

    async createGroup() {
      const groupData = {
        id: "",
        name: this.group.name,
        idCompany: this.idCompany,
        members: {
          transportCompanies: this.selectedTransportCompanies.map((company) => {
            // Busca si ya tiene prioridad configurada
            const existingCompany = this.configuredPriorities?.transportCompanies?.find(tc => 
              tc.identification === company.identification
            );
            return {
              id: company.idCompany || 0,
              name: company.businessName || "",
              identification: company.identification || "",
              nit: company.nit || company.identification || "",
              priority: !!existingCompany?.priority
            };
          }),
          drivers: this.selectedDrivers.map((driver) => {
            // Busca si ya tiene prioridad configurada
            const existingDriver = this.configuredPriorities?.drivers?.find(d => 
              d.identification === driver.identification
            );
            return {
              id: driver.idDriver || 0,
              name: `${driver.name || ""} ${driver.lastName || ""}`.trim(),
              identification: driver.identification || "",
              priority: !!existingDriver?.priority
            };
          }),
          vehicles: this.selectedPlates.map((vehicle) => {
            // Busca si ya tiene prioridad configurada
            const existingVehicle = this.configuredPriorities?.vehicles?.find(v => 
              v.licensePlate === vehicle.licensePlate
            );
            return {
              id: vehicle.idVehicle || 0,
              licensePlate: vehicle.licensePlate || "",
              priority: !!existingVehicle?.priority
            };
          }),
        },
      };


      await this.axios
        .post("/Groups/saveGroup", groupData, {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then(() => {
          this.showSnack({
            text: this.$t("components.views.settings.groups.group_created_successfully"),
            title: this.$t("components.views.settings.groups.success"),
            name: "success",
          });
        });
    },

    async updateGroup() {
      const groupData = {
        id: this.group.id,
        name: this.group.name,
        idCompany: this.group.idCompany,
        members: {
          transportCompanies: this.selectedTransportCompanies.map((company) => {
            // Busca primero en configuredPriorities, si no existe, buscar en this.data
            let existingCompany;
            if (this.configuredPriorities?.transportCompanies) {
              existingCompany = this.configuredPriorities.transportCompanies.find(tc => 
                tc.identification === company.identification
              );
            }
            if (!existingCompany) {
              existingCompany = this.data?.members?.transportCompanies?.find(tc => 
                tc.identification === company.identification || tc.id === company.idCompany
              );
            }
            return {
              id: company.idCompany || 0,
              name: company.businessName || "",
              identification: company.identification || "",
              nit: company.nit || company.identification || 0,
              priority: !!existingCompany?.priority
            };
          }),
          drivers: this.selectedDrivers.map((driver) => {
            // Busca primero en configuredPriorities, si no existe, buscar en this.data
            let existingDriver;
            if (this.configuredPriorities?.drivers) {
              existingDriver = this.configuredPriorities.drivers.find(d => 
                d.identification === driver.identification
              );
            }
            if (!existingDriver) {
              existingDriver = this.data?.members?.drivers?.find(d => 
                d.identification === driver.identification || d.id === driver.idDriver
              );
            }
            return {
              id: driver.idDriver || 0,
              name: `${driver.name || ""} ${driver.lastName || ""}`.trim(),
              identification: driver.identification || "",
              priority: !!existingDriver?.priority
            };
          }),
          vehicles: this.selectedPlates.map((vehicle) => {
            // Busca primero en configuredPriorities, si no existe, buscar en this.data
            let existingVehicle;
            if (this.configuredPriorities?.vehicles) {
              existingVehicle = this.configuredPriorities.vehicles.find(v => 
                v.licensePlate === vehicle.licensePlate
              );
            }
            if (!existingVehicle) {
              existingVehicle = this.data?.members?.vehicles?.find(v => 
                v.licensePlate === vehicle.licensePlate || v.id === vehicle.idVehicle
              );
            }
            return {
              id: vehicle.idVehicle || 0,
              licensePlate: vehicle.licensePlate || "",
              priority: !!existingVehicle?.priority
            };
          }),
        },
      };


      await this.axios
        .post("/Groups/saveGroup", groupData, {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then(() => {
          this.showSnack({
            text: this.$t("components.views.settings.groups.group_updated_successfully"),
            title: this.$t("components.views.settings.groups.success"),
            name: "success",
          });
        });
    },

    async confirmDelete() {
      this.showLoader(true);
      try {
        await this.axios.get(`/Groups/deleteGroup/${this.group.id}`);
        this.showSnack({
          text: this.$t("components.views.settings.groups.group_deleted_successfully"),
          title: this.$t("components.views.settings.groups.success"),
          name: "success",
        });
        this.showDeleteConfirmation = false;
        this.$emit("reloadTable");
        this.$emit("closeForm");
      } catch (error) {
        this.showSnack({
          text: error.response?.data || this.$t("components.views.settings.groups.error"),
          title: this.$t("components.views.settings.groups.error"),
          name: "error",
        });
      } finally {
        this.showLoader(false);
      }
    },

    async openPrioritiesModal() {
      // Si estamos editando, recargar los datos del grupo desde el servidor
      if (this.edit && this.group.id) {
        this.showLoader(true);
        try {
          // Obtener todos los grupos y buscar el actual
          const response = await this.axios.get('/Groups/groupsByIdCompany');
          if (response.data) {
            const updatedGroup = response.data.find(g => g.id === this.group.id);
            if (updatedGroup) {
              // Actualizar this.data con los datos más recientes del servidor
              Object.assign(this.data, updatedGroup);
            }
          }
        } catch (error) {
          console.error('Error reloading group data:', error);
          this.showSnack({
            text: 'Error al cargar los datos del grupo',
            title: 'Error',
            name: 'error',
          });
        } finally {
          this.showLoader(false);
        }
      }
      
      this.prioritiesModalKey += 1;
      this.showPrioritiesModal = true;
    },

    closePrioritiesModal() {
      this.showPrioritiesModal = false;
    },

    buildCurrentMembers() {
      return {
        transportCompanies: this.selectedTransportCompanies.map((company) => ({
          id: parseInt(company.identification) || 0,
          name: company.businessName || "",
          identification: company.identification || "",
          nit: company.nit || company.identification || "",
          priority: false
        })),
        drivers: this.selectedDrivers.map((driver) => ({
          id: driver.idDriver || 0,
          name: `${driver.name || ""} ${driver.lastName || ""}`.trim(),
          identification: driver.identification || "",
          priority: false
        })),
        vehicles: this.selectedPlates.map((vehicle) => ({
          id: vehicle.idVehicle || 0,
          licensePlate: vehicle.licensePlate || "",
          priority: false
        }))
      };
    },

    buildCurrentMembersWithPriorities() {
      // Construir miembros pero manteniendo las prioridades guardadas en configuredPriorities
      return {
        transportCompanies: this.selectedTransportCompanies.map((company) => {
          const existingCompany = this.configuredPriorities?.transportCompanies?.find(tc => 
            tc.identification === company.identification
          );
          return {
            id: parseInt(company.identification) || 0,
            name: company.businessName || "",
            identification: company.identification || "",
            nit: company.nit || company.identification || "",
            priority: !!existingCompany?.priority
          };
        }),
        drivers: this.selectedDrivers.map((driver) => {
          const existingDriver = this.configuredPriorities?.drivers?.find(d => 
            d.identification === driver.identification
          );
          return {
            id: driver.idDriver || 0,
            name: `${driver.name || ""} ${driver.lastName || ""}`.trim(),
            identification: driver.identification || "",
            priority: !!existingDriver?.priority
          };
        }),
        vehicles: this.selectedPlates.map((vehicle) => {
          const existingVehicle = this.configuredPriorities?.vehicles?.find(v => 
            v.licensePlate === vehicle.licensePlate
          );
          return {
            id: vehicle.idVehicle || 0,
            licensePlate: vehicle.licensePlate || "",
            priority: !!existingVehicle?.priority
          };
        })
      };
    },

    async onPrioritiesUpdated(updatedGroupData) {
      // Actualizar los datos locales con las prioridades actualizadas
      // Guardar las prioridades en la variable local
      if (updatedGroupData && updatedGroupData.members) {
        this.configuredPriorities = JSON.parse(JSON.stringify(updatedGroupData.members));
      }
      
      // Recargar la tabla de grupos en el componente padre (solo en modo edición)
      if (this.edit) {
        this.$emit("reloadTable");
      }
    },


  },
  async mounted() {
    // Cargar todos los datos primero
    await this.loadAllData();
    
    if (this.edit) {
      this.group = { ...this.data };
    }
  },
};
</script>

<style scoped>
.title-card {
  background-color: #0b0045 !important;
  color: white !important;
}

.title-card>>>.fa-solid {
  color: #fff !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.v-card-actions {
  padding: 16px 20px;
}

h5 {
  color: #666;
  font-weight: 500;
  margin-bottom: 8px;
}

.section-title {
  color: #0b0045 !important;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 12px;
}

.icon-container {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}
</style>
